import React, { useRef, useState } from 'react';
import { Camera, ExternalLink, Link2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQuery } from '@apollo/client';
import { fileToBase64, validateEntryPhoto } from '../../components/EntryPhotoPicker';
import { getPublicErrorMessage, isLimitReachedError } from '../../utils/errors';
import { entryPhotoStrings as strings } from '../../content/entryPhotos';
import {
  GET_ENTRY_PHOTO_ALBUMS,
  GET_ENTRY_HIGHLIGHT_PHOTOS,
  GET_PHOTO_STORAGE_USAGE,
  ADD_ENTRY_HIGHLIGHT_PHOTO,
  REMOVE_ENTRY_HIGHLIGHT_PHOTO,
  REMOVE_ENTRY_PHOTO_ALBUM_LINK,
  SAVE_ENTRY_PHOTO_ALBUM_LINK,
} from './operations';
import './photoAlbums.css';

const emptyDraft = { id: '', label: '', url: '', visibility: 'owner' };

/** Displays provider-hosted albums without importing their photos into Hobtalk. */
export default function PhotoAlbumsSection({ hobbyId, entryId, isOwner = true, onLimitReached }) {
  const [draft, setDraft] = useState(null);
  const [sharePhoto, setSharePhoto] = useState(false);
  const fileRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState('');
  const { data, loading, error, refetch } = useQuery(GET_ENTRY_PHOTO_ALBUMS, {
    variables: { hobbyId, entryId },
    skip: !entryId,
    fetchPolicy: 'cache-and-network',
  });
  const [saveLink, { loading: saving }] = useMutation(SAVE_ENTRY_PHOTO_ALBUM_LINK);
  const [removeLink, { loading: removing }] = useMutation(REMOVE_ENTRY_PHOTO_ALBUM_LINK);
  const { data: highlightData, refetch: refetchHighlights } = useQuery(GET_ENTRY_HIGHLIGHT_PHOTOS, {
    variables: { hobbyId, entryId }, skip: !entryId, fetchPolicy: 'cache-and-network',
  });
  const { data: usageData, refetch: refetchUsage } = useQuery(GET_PHOTO_STORAGE_USAGE, { skip: !isOwner });
  const [addHighlight, { loading: uploading }] = useMutation(ADD_ENTRY_HIGHLIGHT_PHOTO);
  const [removeHighlight] = useMutation(REMOVE_ENTRY_HIGHLIGHT_PHOTO);
  const links = data?.getEntryPhotoAlbums || [];
  const highlights = highlightData?.getEntryHighlightPhotos || [];
  const usage = usageData?.getPhotoStorageUsage;

  const uploadPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const validation = validateEntryPhoto(file);
    if (validation) { setErrorMessage(validation); return; }
    setErrorMessage('');
    try {
      const base64Data = await fileToBase64(file);
      await addHighlight({ variables: { input: { hobbyId, entryId, contentType: file.type, base64Data, visibility: sharePhoto ? 'shared' : 'owner' } } });
      await Promise.all([refetchHighlights(), refetchUsage()]);
    } catch (uploadError) {
      setErrorMessage(getPublicErrorMessage(uploadError));
      if (isLimitReachedError(uploadError)) onLimitReached?.();
    }
  };

  const deletePhoto = async (photo) => {
    if (!window.confirm(strings.removePhotoPrompt)) return;
    setErrorMessage('');
    try {
      await removeHighlight({ variables: { hobbyId, entryId, id: photo.id } });
      await Promise.all([refetchHighlights(), refetchUsage()]);
    } catch (removeError) { setErrorMessage(getPublicErrorMessage(removeError)); }
  };

  const save = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    try {
      await saveLink({ variables: { input: { hobbyId, entryId, ...draft, id: draft.id || null } } });
      setDraft(null);
      await refetch();
    } catch (saveError) {
      setErrorMessage(getPublicErrorMessage(saveError));
    }
  };

  const remove = async (link) => {
    if (!window.confirm(strings.removeAlbumPrompt(link.label))) return;
    setErrorMessage('');
    try {
      await removeLink({ variables: { hobbyId, entryId, id: link.id } });
      await refetch();
    } catch (removeError) {
      setErrorMessage(getPublicErrorMessage(removeError));
    }
  };

  if (!isOwner && !loading && !error && links.length === 0 && highlights.length === 0) return null;

  return (
    <section className="recipe-detail-panel photo-albums-section">
      {(isOwner || highlights.length > 0) && <>
        <div className="photo-albums-heading">
          <h3>{strings.highlights}</h3>
          {isOwner && highlights.length < 4 && <button type="button" className="photo-albums-icon-button" title={strings.addHighlight} aria-label={strings.addHighlight} disabled={uploading} onClick={() => fileRef.current?.click()}><Camera size={18} /></button>}
        </div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={uploadPhoto} />
        {highlights.length > 0 && <div className="entry-highlights-grid">
          {highlights.map((photo) => <div className="entry-highlight" key={photo.id}>
            <img src={photo.url} alt="Trip or hike highlight" />
            {isOwner && <button type="button" className="photo-albums-icon-button danger" title={strings.removeHighlight} aria-label={strings.removeHighlight} onClick={() => deletePhoto(photo)}><Trash2 size={16} /></button>}
          </div>)}
        </div>}
        {isOwner && hobbyId === 'traveling' && <label className="photo-album-check"><input type="checkbox" checked={sharePhoto} onChange={(event) => setSharePhoto(event.target.checked)} />{strings.shareHighlights}</label>}
        {isOwner && usage && <small className="photo-storage-usage">{strings.storage(Math.round(usage.usedBytes / 1048576), Math.round(usage.limitBytes / 1048576))}</small>}
      </>}
      <div className="photo-albums-heading">
        <h3>{strings.albums}</h3>
        {isOwner && !draft && links.length < 3 && (
          <button type="button" className="photo-albums-icon-button" title={strings.addAlbumLink} aria-label={strings.addAlbumLink} onClick={() => setDraft(emptyDraft)}><Plus size={18} /></button>
        )}
      </div>
      {loading && !data && <p className="detail-notes">Loading albums...</p>}
      {error && <p className="form-error">{getPublicErrorMessage(error)}</p>}
      {!loading && !error && links.length === 0 && isOwner && <p className="detail-notes">{strings.emptyAlbums}</p>}
      {links.map((link) => (
        <div className="photo-album-row" key={link.id}>
          <Link2 size={18} aria-hidden="true" />
          <a href={link.url} target="_blank" rel="noopener noreferrer" title={`Open ${link.label}`}>
            <strong>{link.label}</strong><small>{link.provider.replaceAll('_', ' ')}</small>
          </a>
          <ExternalLink size={16} aria-hidden="true" />
          {isOwner && <>
            <button type="button" className="photo-albums-icon-button" title={`Edit ${link.label}`} aria-label={`Edit ${link.label}`} onClick={() => setDraft({ id: link.id, label: link.label, url: link.url, visibility: link.visibility })}><Pencil size={16} /></button>
            <button type="button" className="photo-albums-icon-button danger" title={`Remove ${link.label}`} aria-label={`Remove ${link.label}`} disabled={removing} onClick={() => remove(link)}><Trash2 size={16} /></button>
          </>}
        </div>
      ))}
      {draft && <form className="photo-album-form" onSubmit={save}>
        <label>{strings.albumName}<input required maxLength={80} value={draft.label} onChange={(event) => setDraft({ ...draft, label: event.target.value })} placeholder="Trip photos" /></label>
        <label>{strings.albumLink}<input required type="url" value={draft.url} onChange={(event) => setDraft({ ...draft, url: event.target.value })} placeholder="https://" /></label>
        {hobbyId === 'traveling' && <label className="photo-album-check"><input type="checkbox" checked={draft.visibility === 'shared'} onChange={(event) => setDraft({ ...draft, visibility: event.target.checked ? 'shared' : 'owner' })} />{strings.sharedAlbum}</label>}
        <small className="photo-storage-usage">{strings.albumStorageNote}</small>
        <div className="photo-album-actions"><button type="button" className="btn-ghost" onClick={() => setDraft(null)}>Cancel</button><button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : strings.saveAlbum}</button></div>
      </form>}
      {errorMessage && <p className="form-error" role="alert">{errorMessage}</p>}
    </section>
  );
}
